import { useEffect, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";
import { EntityTile, type EntityItem } from "@/components/MultiEntityPicker";
import { SyncGlyph } from "./SyncGlyph";

/** Single-select entity picker with the same tile visuals as
 *  MultiEntityPicker — app icon (iTunes artwork), name, sub, chevron.
 *  Used by the Ads Manager app filter where only one selection is
 *  meaningful at a time. */
export function SingleEntityPicker<T extends string | number>({
  items,
  value,
  onChange,
  label,
  width,
  allOption,
  onRefresh,
  isRefreshing,
  syncNow,
}: {
  items: EntityItem[];
  value: T;
  onChange: (id: T) => void;
  label?: string;
  width?: number;
  /** Optional synthetic "All" row. Rendered at the top of the popover
   *  and as the trigger when selected. */
  allOption?: { id: T; name: string; sub?: string };
  /** Optional refresh button in the popover header. Wired e.g. to
   *  `useRefreshAppsFromAsa()` to re-discover newly added apps in
   *  the ASA dashboard without waiting for the next collector tick. */
  onRefresh?: () => void | Promise<void>;
  /** Spinner-state for the refresh button when prop is wired. */
  isRefreshing?: boolean;
  /** Optional sticky footer "Synchronize Now" button (§37). Mirrors
   *  MultiEntityPicker's `syncNow` prop. Same handler shape so the
   *  caller can wire both pickers from the same `useRefreshAppsFromAsa`
   *  hook. Prefer this over the header refresh button — the explicit
   *  label + status copy is friendlier than a tiny refresh glyph. */
  syncNow?: {
    onClick: () => void;
    loading: boolean;
    label?: string;
    status?: string | null;
  };
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayer(open, () => setOpen(false));

  const allTile: EntityItem = allOption
    ? {
        id: allOption.id,
        name: allOption.name,
        sub: allOption.sub,
        tint: "linear-gradient(135deg,#6e62ff,#2fb46f)",
        initials: "\u2217",
      }
    : ({} as EntityItem);

  const isAll = allOption && value === allOption.id;
  const current: EntityItem | null = isAll
    ? allTile
    : items.find((i) => i.id === value) || null;

  return (
    <div
      ref={ref}
      style={{ position: "relative", display: "inline-block", minWidth: width || 220 }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="app-picker"
        data-open={open}
        style={{ width: "100%", minWidth: 0 }}
      >
        {current ? (
          <EntityTile item={current} />
        ) : (
          <span className="app-tile" style={{ background: "var(--c-text-4)" }}>?</span>
        )}
        <span className="app-meta" style={{ minWidth: 0 }}>
          {label && (
            <span
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "var(--c-text-4)",
                display: "block",
                lineHeight: 1.1,
              }}
            >
              {label}
            </span>
          )}
          <span className="app-name">{current?.name ?? "—"}</span>
          {current?.sub && <span className="app-sub">{current.sub}</span>}
        </span>
        <span className="app-chev">{I.chevronDown}</span>
      </button>
      {open && (
        <div
          className="app-popover"
          style={{ minWidth: width ? width + 40 : 300, padding: 6 }}
        >
          {(label || onRefresh) && (
            <div
              className="app-popover-head"
              style={{
                padding: "8px 10px 6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>{label}</span>
              {onRefresh && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void onRefresh();
                  }}
                  disabled={isRefreshing}
                  title="Refresh apps from ASA — discovers newly added apps in your Apple Search Ads account"
                  style={{
                    background: "transparent",
                    border: "1px solid var(--c-border, #ddd)",
                    borderRadius: 6,
                    padding: "3px 8px",
                    cursor: isRefreshing ? "wait" : "pointer",
                    fontSize: 11,
                    color: isRefreshing
                      ? "var(--c-text-4, #aaa)"
                      : "var(--c-text-2, #666)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      transform: isRefreshing
                        ? "rotate(360deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.6s linear",
                      animation: isRefreshing
                        ? "spin 1s linear infinite"
                        : "none",
                    }}
                  >
                    ↻
                  </span>
                  {isRefreshing ? "Refreshing…" : "Refresh"}
                </button>
              )}
            </div>
          )}
          {allOption && (
            <button
              className="app-opt"
              data-active={isAll || undefined}
              onClick={() => {
                onChange(allOption.id);
                setOpen(false);
              }}
            >
              <EntityTile item={allTile} size="sm" />
              <span className="app-meta">
                <span className="app-name">{allOption.name}</span>
                {allOption.sub && <span className="app-sub">{allOption.sub}</span>}
              </span>
              {isAll && <span style={{ color: "var(--c-accent-600)" }}>{I.check}</span>}
            </button>
          )}
          {items.map((a) => {
            const active = a.id === value;
            return (
              <button
                key={String(a.id)}
                className="app-opt"
                data-active={active || undefined}
                onClick={() => {
                  onChange(a.id as T);
                  setOpen(false);
                }}
              >
                <EntityTile item={a} size="sm" />
                <span className="app-meta">
                  <span className="app-name">{a.name}</span>
                  {a.sub && <span className="app-sub">{a.sub}</span>}
                </span>
                {active && (
                  <span style={{ color: "var(--c-accent-600)" }}>{I.check}</span>
                )}
              </button>
            );
          })}
          {syncNow && (
            <>
              <div
                style={{
                  height: 1,
                  background: "var(--c-divider)",
                  margin: "6px 2px 4px",
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  // See MultiEntityPicker — don't close the popover
                  // so the user sees the discovered-count status copy
                  // inline as confirmation.
                  e.preventDefault();
                  e.stopPropagation();
                  if (!syncNow.loading) syncNow.onClick();
                }}
                disabled={syncNow.loading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  background: "transparent",
                  border: "none",
                  borderRadius: 6,
                  cursor: syncNow.loading ? "wait" : "pointer",
                  color: syncNow.loading
                    ? "var(--c-text-3)"
                    : "var(--c-accent-600)",
                  fontSize: 13,
                  fontWeight: 500,
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (!syncNow.loading) {
                    e.currentTarget.style.background =
                      "var(--c-surface-sunk)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <SyncGlyph spinning={syncNow.loading} />
                <span>
                  {syncNow.loading
                    ? "Synchronizing…"
                    : syncNow.label ?? "Synchronize Now"}
                </span>
              </button>
              {syncNow.status && (
                <div
                  style={{
                    padding: "2px 10px 6px 32px",
                    fontSize: 11.5,
                    color: "var(--c-text-3)",
                  }}
                >
                  {syncNow.status}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
